import React, { useEffect, useState } from 'react';
import createClass from 'create-react-class';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@welldev-group/rcl-button';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import ContentLoaderPlaceholder from '@welldev-group/rcl-content-loader-placeholder';
import { I18nText, EmphasisTag, Amount } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import Modal from '@welldev-group/rcl-modal';
import Link from '@welldev-group/rcl-link';
import Tooltip from '@welldev-group/rcl-tooltip';
import creditCard from 'affiliateIcons/credit-card.svg';
import electric from 'affiliateIcons/electric.svg';
import money from 'affiliateIcons/money.svg';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import SanitizedHTML from '../../../shared/components/SanitizedHTML';

import TransactionListOverview from './TransactionListOverview';
import TopUpModal from './TopUpModal';
import DateTime from '../DateTime';
import AddNewCreditCard from './AddNewCreditCard';
import CreditCard from './CreditCard';
import CancelScheduledDowngradeModule from './Subcription/CancelScheduledDowngradeModule';

const Billing = ({
  affiliateId,
  transactionsUrl,
  billingUrl,
  ccvUrl,
  ccvPublicToken,
  ccvIntegrityHash,
}) => {
  const INITIAL_NUMBER_OF_CARDS_TO_SHOW = 3;

  const [creditCards, setCreditCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [billingData, setBillingData] = useState(null);

  const [numberOfCardsToShow, setNumberOfCardsToShow] = useState(
    INITIAL_NUMBER_OF_CARDS_TO_SHOW,
  );

  const onShowMoreClick = () => {
    setNumberOfCardsToShow(previousState => previousState + 4);
  };

  const onShowLessClick = () => {
    setNumberOfCardsToShow(INITIAL_NUMBER_OF_CARDS_TO_SHOW);
  };

  const onModalClose = () => {
    setModalOpen(false);
  };

  const onModalOpen = () => {
    setModalOpen(true);
  };

  const fetchBillingData = async () => {
    const { data } = await httpClient.get(
      routes.billing.fetch({
        affiliate_id: affiliateId,
      }),
    );
    setBillingData(data);
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  const smallCardLoader = (
    <ContentLoaderPlaceholder numberOfLines={4} showBackground={true} />
  );

  const pendingPaymentCard =
    billingData &&
    billingData.billingKpis &&
    billingData.billingKpis.pendingPayments.amount > 0 ? (
      <Card version="v2" className="billing__pending billing__pending-payment">
        <div className="billing__pending-title">
          <span>
            <I18nText id="billing.pending.title" />
            <div className="billing__pending-amount">
              <Amount
                locale="de-CH"
                value={billingData.billingKpis.pendingPayments.amount}
                currency={billingData.billingKpis.pendingPayments.currency}
              />
            </div>
          </span>
          <div className="billing__pending-show">
            <Link
              href={`${transactionsUrl}?tab=transactions`}
              size="small"
              type="anchor"
              modifier="tertiary"
            >
              <I18nText id="billing.pending.view_all" />
            </Link>
          </div>
        </div>
        <div className="billing__pending-text">
          <span>
            <I18nText id="billing.pending.description" />
          </span>
        </div>
      </Card>
    ) : (
      <Card version="v2" className="billing__pending">
        <div className="billing__pending-title">
          <I18nText id="billing.pending.title" />
        </div>
        <div className="billing__pending-icon">
          <img src={money} alt="pending-payments" />
        </div>
        <div className="billing__pending-empty-text">
          <I18nText id="billing.pending.empty" />
        </div>
      </Card>
    );

  const latestTopUpCard =
    billingData &&
    billingData.billingKpis &&
    billingData.billingKpis.latestTopUps.length ? (
      <Card version="v2" className="billing__top-up">
        <div className="billing__top-up-title">
          <I18nText id="billing.top_ups.title" />
        </div>
        {billingData.billingKpis.latestTopUps.map(paidInvoice => (
          <div className="billing__top-up-content" key={paidInvoice.id}>
            <span className="billing__top-up-content-date">
              <DateTime dateTime={paidInvoice.date} format="short" />
            </span>
            <span className="billing__top-up-content-amount">
              +
              <Amount
                locale="de-CH"
                value={paidInvoice.amount}
                currency={paidInvoice.currency}
              />
            </span>
          </div>
        ))}
      </Card>
    ) : (
      <Card version="v2" className="billing__top-up">
        <div className="billing__top-up-title">
          <I18nText id="billing.top_ups.title" />
        </div>
        <div className="billing__top-up-icon">
          <img src={electric} alt="top-up" />
        </div>
        <div className="billing__top-up-empty-text">
          <I18nText id="billing.top_ups.empty" />
        </div>
      </Card>
    );

  const executeNetworkCall = async () => {
    const data = await httpClient.get(
      routes.billing.credit_cards.list({
        affiliate_id: affiliateId,
      }),
    );

    return data;
  };

  const fetchCreditCards = async () => {
    setLoading(true);
    const { data } = await executeNetworkCall();
    setCreditCards(data.stripeCreditCards);
    setLoading(false);
  };

  let pollingCount = 0;

  const executeCreditCardPoll = async () => {
    const { data } = await executeNetworkCall();
    setCreditCards(data.stripeCreditCards);

    // we are executing this function 9 times after the initial with an interval of 5 seconds
    // So that the polling remains active for 1 minute. This will get executed only after adding a card
    if (pollingCount < 9) {
      setTimeout(executeCreditCardPoll, 5000);
      pollingCount += 1;
    } else {
      pollingCount = 0;
    }
  };

  useEffect(() => {
    window.React = React;
    window.createReactClass = createClass;

    const script = document.createElement('script');
    script.src = `${ccvUrl}/assets/v2-welldev-credit-card.js`;
    script.integrity = ccvIntegrityHash;
    script.crossOrigin = 'anonymous';
    script.async = true;
    document.head.appendChild(script);
    fetchCreditCards();
  }, []);

  const noCardPresentView = (
    <>
      <div className="billing__credit-card-icon">
        <img src={creditCard} alt="credit-card" />
      </div>
      <div className="billing__credit-card-empty-text">
        <I18nText id="billing.payment_details.empty" />
      </div>
      <div className="billing__credit-card-information-note">
        <I18nText
          id="billing.payment_details.information_note"
          returnStringOnly={true}
        />
      </div>
    </>
  );

  const primaryCreditCard =
    creditCards && creditCards.find(card => card.primary);
  const secondaryCreditCards =
    creditCards && creditCards.filter(card => !card.primary);
  const showShowLessButton =
    secondaryCreditCards &&
    secondaryCreditCards.length > INITIAL_NUMBER_OF_CARDS_TO_SHOW &&
    secondaryCreditCards &&
    secondaryCreditCards.length <= numberOfCardsToShow;

  return (
    <>
      <div className="container-full">
        <div className="billing__header">
          <I18nText id="billing.header" />
        </div>
        <Alert
          className="billing__alert"
          isVisible={billingData && billingData.chargeFailing}
          hideClose={true}
          type="danger"
        >
          <I18nText id="billing.charge_failed_alert" />
        </Alert>
        <div className="grid">
          <div className="col-12 col-bleed-y">
            <CancelScheduledDowngradeModule
              affiliateId={affiliateId}
              scheduledDowngrade={billingData && billingData.scheduledDowngrade}
              redirectTo={billingUrl}
            />
          </div>
          <div className="col-12 col-md-6 col-xlg-3">
            {!billingData ? (
              smallCardLoader
            ) : (
              <Card version="v2" className="billing__balance">
                <div className="billing__balance-wrapper">
                  <span>
                    <div className="billing__balance-title">
                      <I18nText id="billing.balance.total" />
                    </div>
                    <div className="billing__balance-amount">
                      <div className="billing__balance-amount-value">
                        <Amount
                          locale="de-CH"
                          value={billingData.totalBalance}
                          currency={billingData.currency}
                          minFractionDigit={2}
                        />
                      </div>
                      {billingData.hasDefaultCurrency && (
                        <div className="billing__balance-amount-value">
                          <Tooltip
                            content={
                              <SanitizedHTML
                                html={I18n.t(
                                  'components.billing.balance.fallback',
                                )}
                              />
                            }
                            type="inverse"
                          >
                            <Icon color="tertiary" name="info" size="small" />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </span>
                  <div className="billing__balance-top-up-modal">
                    <TopUpModal
                      currentBalance={billingData && billingData.totalBalance}
                      currency={billingData && billingData.currency}
                      affiliateId={affiliateId}
                      disableCreditCardOption={
                        creditCards && creditCards.length === 0
                      }
                      primaryCreditCard={primaryCreditCard}
                      creditCards={creditCards}
                      stripeSuccessStatus={
                        billingData && billingData.stripeSuccessStatus
                      }
                    />
                  </div>
                </div>
                <div className="billing__balance-action">
                  <div className="billing__balance-footer">
                    <div className="billing__balance-title billing__balance-available">
                      <I18nText id="billing.balance.available" />
                    </div>
                    <div className="billing__balance-footer-amount">
                      <Amount
                        locale="de-CH"
                        value={billingData && billingData.availableBalance}
                        currency={billingData && billingData.currency}
                        minFractionDigit={2}
                      />
                    </div>
                  </div>
                  <div className="billing__balance-footer billing__balance-footer-top-up">
                    <div className="billing__balance-title">
                      <I18nText id="billing.balance.reserved" />
                    </div>
                    <div className="billing__balance-footer-amount">
                      <Amount
                        locale="de-CH"
                        value={billingData && billingData.reservedBalance}
                        currency={billingData && billingData.currency}
                        minFractionDigit={1}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
          <div className="col-12 col-md-6 col-xlg-3">
            {!billingData ? smallCardLoader : latestTopUpCard}
          </div>
          <div className="col-12 col-md-6 col-xlg-3">
            {!billingData ? smallCardLoader : pendingPaymentCard}
          </div>
          <div className="col-12 col-md-6 col-xlg-3">
            {!billingData ? (
              smallCardLoader
            ) : (
              <Card
                version="v2"
                className="billing__current-plan billing__pending-payment"
              >
                <div className="billing__current-plan-header">
                  <div className="billing__current-plan-header-text">
                    {I18n.t('components.billing.plan.title')}
                  </div>
                  <Link
                    className="billing__current-plan-header-link"
                    href={billingData && billingData.plansUrl}
                    modifier="tertiary"
                  >
                    {I18n.t('components.billing.plan.link_view')}
                  </Link>
                </div>
                <div className="billing__current-plan-info">
                  {billingData && billingData.currentPlan && (
                    <div className="billing__current-plan-info-name">
                      {I18n.t(
                        billingData &&
                          billingData.currentPlan &&
                          billingData.currentPlan.name,
                        {
                          scope: 'components.plans',
                        },
                      )}
                    </div>
                  )}
                  {!billingData && billingData.automaticBillingDisabled && (
                    <EmphasisTag
                      className="billing__current-plan-info-period"
                      text={
                        billingData &&
                        billingData.currentPlan &&
                        billingData.currentPlan.period
                      }
                      type="neutral"
                      size="tiny"
                    />
                  )}
                </div>
                <div className="billing__current-plan-affiliate-info">
                  <div className="billing__current-plan-affiliate-info-total-users">
                    <div className="billing__current-plan-affiliate-info-total-users-title">
                      {I18n.t('components.billing.plan.total_active_users')}
                    </div>
                    {billingData && billingData.totalActiveUsers}
                  </div>
                  <div className="billing__current-plan-affiliate-info-next-billing-date">
                    <div className="billing__current-plan-affiliate-info-next-billing-date-title">
                      {I18n.t('components.billing.plan.next_billing_on')}
                    </div>
                    {billingData && billingData.automaticBillingDisabled ? (
                      <div className="billing__current-plan-affiliate-info-next-billing-date-disabled-billing-text">
                        {I18n.t(
                          'components.billing.plan.disabled_automatic_billing_text',
                        )}
                      </div>
                    ) : (
                      <DateTime
                        dateTime={billingData && billingData.nextBillingDate}
                        format="short"
                      />
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
        <div className="grid">
          <div className="col-lg-3 col-md-4 col-12">
            <Card version="v2" className="billing__credit-card">
              <div className="billing__credit-card-title">
                <I18nText id="billing.payment_details.title" />
              </div>
              {loading ? (
                <ContentLoaderPlaceholder
                  numberOfLines={12}
                  showBackground={false}
                />
              ) : (
                <>
                  {creditCards && creditCards.length === 0 && noCardPresentView}
                  {creditCards && creditCards.length > 0 && (
                    <>
                      <div className="billing__credit-card-primary-title">
                        <I18nText id="billing.payment_details.primary" />
                      </div>
                      <CreditCard
                        id={primaryCreditCard.id}
                        lastFour={primaryCreditCard.lastFour}
                        cardType={primaryCreditCard.cardType}
                        cardholderName={primaryCreditCard.cardholderName}
                        disableAction={primaryCreditCard.primary}
                        affiliateId={affiliateId}
                        fetchCreditCards={fetchCreditCards}
                      />
                      <div className="billing__credit-card-information-note--with-margin-bottom">
                        <I18nText
                          id="billing.payment_details.information_note"
                          returnStringOnly={true}
                        />
                      </div>
                    </>
                  )}
                  <div
                    className={classNames('billing__credit-card-action', {
                      'billing__credit-card-action--margin':
                        creditCards && creditCards.length === 0,
                      'billing__credit-card-action--cards-available':
                        creditCards && creditCards.length > 0,
                    })}
                  >
                    <Button
                      version="v2"
                      size="small"
                      fullWidth={false}
                      type="tertiary"
                      label={<I18nText id="billing.payment_details.add_card" />}
                      onClick={onModalOpen}
                    />
                  </div>
                  {secondaryCreditCards && secondaryCreditCards.length > 0 && (
                    <>
                      <div className="billing__credit-card-secondary-title">
                        <I18nText id="billing.payment_details.secondary" />
                      </div>
                      <div>
                        {secondaryCreditCards
                          .slice(0, numberOfCardsToShow)
                          .map(card => (
                            <CreditCard
                              key={card.id}
                              id={card.id}
                              lastFour={card.lastFour}
                              cardType={card.cardType}
                              cardholderName={card.cardholderName}
                              disableAction={card.primary}
                              affiliateId={affiliateId}
                              fetchCreditCards={fetchCreditCards}
                            />
                          ))}
                        {secondaryCreditCards &&
                          secondaryCreditCards.length > numberOfCardsToShow && (
                            <div className="col-12 col-bleed">
                              <div
                                role="presentation"
                                className="col-grid direction-row col-bleed align-center justify-center billing__credit-card-show-more"
                                onClick={onShowMoreClick}
                              >
                                <I18nText id="billing.payment_details.show_more" />
                                <div className="billing__credit-card-show-more-icon">
                                  <Icon name="iconDownChevron" size="tiny" />
                                </div>
                              </div>
                            </div>
                          )}
                        {showShowLessButton && (
                          <div className="col-12 col-bleed">
                            <div
                              role="presentation"
                              className="col-grid direction-row col-bleed align-center justify-center billing__credit-card-show-less"
                              onClick={onShowLessClick}
                            >
                              <I18nText id="billing.payment_details.show_less" />
                              <div className="billing__credit-card-show-more-icon">
                                <Icon name="iconUpChevron" size="tiny" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </Card>
          </div>
          <div className="col-lg-9 col-md-8 col-12">
            <Card version="v2" className="billing__transactions">
              <div>
                <div className="billing__transactions-title">
                  <I18nText id="billing.payment_details.history" />
                </div>
                <TransactionListOverview affiliateId={affiliateId} />
              </div>
              <a
                className="button button--v2 button--small billing-transaction-list__history-link"
                href={`${transactionsUrl}`}
              >
                <I18nText id="billing.transactions.link_label_all" />
              </a>
            </Card>
          </div>
        </div>
      </div>
      {billingData && billingData.affiliateIdentifier && (
        <Modal
          className="modal__wrapper"
          isVisible={modalOpen}
          onOutsideClick={onModalClose}
        >
          <div className="col-12 col-bleed-y modal__header">
            <Modal.Title>
              <I18nText id="billing.add_credit_card.modal_header" />
            </Modal.Title>
            <div
              onClick={onModalClose}
              onKeyDown={onModalClose}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <div>
            <Modal.Content>
              <div>
                <AddNewCreditCard
                  ccvUrl={ccvUrl}
                  ccvPublicToken={ccvPublicToken}
                  closeModal={onModalClose}
                  affiliateIdentifier={billingData.affiliateIdentifier}
                  affiliateId={affiliateId}
                  fetchCreditCards={executeCreditCardPoll}
                />
              </div>
            </Modal.Content>
          </div>
        </Modal>
      )}
    </>
  );
};

Billing.propTypes = {
  transactionsUrl: PropTypes.string.isRequired,
  affiliateId: PropTypes.number.isRequired,
  billingUrl: PropTypes.string.isRequired,
  ccvPublicToken: PropTypes.string.isRequired,
  ccvUrl: PropTypes.string.isRequired,
  ccvIntegrityHash: PropTypes.string.isRequired,
};

export default Billing;
